import { CURRENT_EVENT_SLUG } from "src/shared/constants/event";

export const AUTOSUGGEST_CACHE_TTL_MS = 60 * 60 * 1000;

type AutosuggestCacheStorage = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem"
>;

interface AutosuggestCacheEntry {
  cachedAt: number;
  claimIds: number[];
}

export const getAutosuggestCacheKey = (claimId: number): string =>
  `hacker-directory:auto-suggest:${CURRENT_EVENT_SLUG}:${claimId}`;

const getBrowserStorage = (): AutosuggestCacheStorage | null => {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const isClaimId = (claimId: unknown): claimId is number =>
  typeof claimId === "number" && Number.isInteger(claimId) && claimId >= 0;

const normalizeClaimIds = (claimIds: number[]): number[] =>
  Array.from(new Set(claimIds.filter(isClaimId)));

const isAutosuggestCacheEntry = (
  value: unknown
): value is AutosuggestCacheEntry => {
  if (!value || typeof value !== "object") return false;

  const entry = value as Partial<AutosuggestCacheEntry>;
  return (
    typeof entry.cachedAt === "number" &&
    Number.isFinite(entry.cachedAt) &&
    Array.isArray(entry.claimIds) &&
    entry.claimIds.every(isClaimId)
  );
};

const removeCacheEntry = (
  storage: AutosuggestCacheStorage,
  cacheKey: string
) => {
  try {
    storage.removeItem(cacheKey);
  } catch {
    // Storage can be unavailable in private browsing or restricted contexts.
  }
};

export const readAutosuggestClaimIds = (
  claimId: number,
  now = Date.now(),
  storage: AutosuggestCacheStorage | null = getBrowserStorage()
): number[] | null => {
  if (!storage) return null;

  const cacheKey = getAutosuggestCacheKey(claimId);
  try {
    const serializedEntry = storage.getItem(cacheKey);
    if (!serializedEntry) return null;

    const entry: unknown = JSON.parse(serializedEntry);
    if (
      !isAutosuggestCacheEntry(entry) ||
      entry.cachedAt > now ||
      now - entry.cachedAt >= AUTOSUGGEST_CACHE_TTL_MS
    ) {
      removeCacheEntry(storage, cacheKey);
      return null;
    }

    return normalizeClaimIds(entry.claimIds);
  } catch {
    removeCacheEntry(storage, cacheKey);
    return null;
  }
};

export const writeAutosuggestClaimIds = (
  claimId: number,
  claimIds: number[],
  cachedAt = Date.now(),
  storage: AutosuggestCacheStorage | null = getBrowserStorage()
) => {
  if (!storage) return;

  const entry: AutosuggestCacheEntry = {
    cachedAt,
    claimIds: normalizeClaimIds(claimIds),
  };

  try {
    storage.setItem(getAutosuggestCacheKey(claimId), JSON.stringify(entry));
  } catch {
    // Auto-Suggest still works when storage is full or unavailable.
  }
};
